import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { HOST } from '@perpay-web/constants/urls';
import FintechGlobalProviders from '@perpay-web/fintech/components/base/FintechGlobalProviders/FintechGlobalProviders';
import { QueryClientProvider } from '@perpay-web/fintech/components/base/QueryClientProvider/QueryClientProvider';
import BottomNavContainer from '@perpay-web/fintech/components/containers/BottomNavContainer';
// initialize router
import unusedRouter from '@perpay-web/fintech/components/screens/App/Routes/router';
import store from '@perpay-web/fintech/settings/configureStore';
import { useFullBottomNav } from '@perpay-web/hooks/useFullBottomNavContext';
import { STATUS_BAR_HEIGHT_ANDROID } from '@perpay-web/native-client/constants/statusBar';
import { getIsReactNative } from '@perpay-web/native-client/getIsReactNative';
import { initDatadogClient } from '@perpay-web/services/datadog';
import { AppRouterProvider } from '@perpay-web/services/router';
import '@perpay-web/services/sentry';
import { initializeSentryClient } from '@perpay-web/services/sentry';
import { storeUtmCookie } from '@perpay-web/utils/marketingUtils';
import {
    isAndroidAgent,
    isAppBrowserAgent,
    isIosAgent,
} from '@perpay-web/utils/userAgentUtils';

import './assets/images/favicon.png';
import './index.scss';

if (window.PRODUCTION || window.STAGING) {
    const buildVersionParts = window.VERSION.split('@');
    const buildVersion =
        buildVersionParts.length === 2 ? buildVersionParts[1] : window.VERSION;
    initDatadogClient(buildVersion);
}
initializeSentryClient();

storeUtmCookie(HOST);

const NATIVE_CLASSNAME = 'native';
const IOS_CLASSNAME = 'native--ios';
const ANDROID_CLASSNAME = 'native--android';
const REACT_NATIVE_CLASSNAME = 'native--react-native';
document.documentElement.classList.toggle(
    NATIVE_CLASSNAME,
    isAppBrowserAgent(),
);
document.documentElement.classList.toggle(IOS_CLASSNAME, isIosAgent());
document.documentElement.classList.toggle(ANDROID_CLASSNAME, isAndroidAgent());
document.documentElement.classList.toggle(
    REACT_NATIVE_CLASSNAME,
    getIsReactNative(),
);

if (isAndroidAgent()) {
    document.body.style.setProperty(
        '--status-bar-height-android',
        `${STATUS_BAR_HEIGHT_ANDROID}px`,
    );
}

const container = document.getElementById('root');
const root = createRoot(container, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        if (window.PRODUCTION) {
            return;
        }
        // eslint-disable-next-line no-console
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});

const BottomNav = () => {
    const bottomNavCb = useCallback(() => <BottomNavContainer />, []);
    useFullBottomNav(bottomNavCb);
    return null;
};

root.render(
    <QueryClientProvider>
        <Provider store={store}>
            <FintechGlobalProviders>
                <BottomNav />
                <AppRouterProvider />
            </FintechGlobalProviders>
        </Provider>
    </QueryClientProvider>,
);
